import React, { useCallback, useEffect, useMemo, useState } from "react";
import TreeItem from "@material-ui/lab/TreeItem";
import TreeView from "@material-ui/lab/TreeView";
import { groupBy } from "lodash";
import type { FC, ChangeEvent } from "react";
import AddBoxOutlinedIcon from "../../../../../icons/OpenTree";
import RemoveOutlinedIcon from "../../../../../icons/CloseTree";
import { Hierarchy } from "../../../../common";
import { useStyles, useParentStyles, useItemStyles, useDisabledItemStyles } from "./SavedSettingsTree.styles";
import type { ITreeControlItem, ITreeProps } from "./SavedSettingsTree.types";
import { removeCapitalization } from "../../../../../utils";
import { useBulkExportContext } from "../../../providers";
import { Dialog } from "../Dialog";
import { useSaveSettingsButton } from "../../SaveBulkExportStateButton/SaveBulkExportStateButton.state";
import { BulkExportFormatEnum } from "../../../api/mutations/saveBulkExportState/saveBulkExportState.types";
import { BulkExportFormat } from "../../../types";
import { virtualConfiguration } from "../../BulkExportStepper";
import { StarBorderRounded, StarRounded } from "@material-ui/icons";
import { FavouriteItemType } from "../../../../favourites/types";
import { Button } from "@material-ui/core";

export const SavedSettingsTree: FC<ITreeProps> = ({
  value,
  onChange,
  items,
  expandedAll,
  search,
  addFavourite,
  removeFavourite,
  favouriteItemId,
}) => {
  const context = useBulkExportContext();
  const { saveExistExportSettings } = useSaveSettingsButton();

  const setDataSourceId = context?.actions.setDataSourceId;
  const showSaveNotification = context?.state.showSaveNotification || false;
  const actionAfterSaveCancel = context?.state.actionAfterSaveCancel;
  const settingsWasModified = context?.state.settingsWasModified;
  const savedExportSettingsId = context?.state.savedExportSettingsId;

  const setShowSaveNotification = context?.actions.setShowSaveNotification;
  const setSettingsWasModified = context?.actions.setSettingsWasModified;
  const setActionAfterSaveCancel = context?.actions.setActionAfterSaveCancel;
  const setDimension = context?.actions.setDimension!;
  const setFormat = context?.actions.setFormat!;
  const dataSourceId = context?.state.dataSourceId;
  const selectedItem = items.find((it) => it.savingId === value) || items[0];
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState<string[]>(() => {
    const selectedItemParentId = selectedItem?.parentId;
    const mainParentId = items.find((el) => el.id === selectedItemParentId)?.parentId;

    return selectedItemParentId !== undefined ? [String(selectedItemParentId), String(mainParentId)] : ([] as string[]);
  });
  useEffect(() => {
    const configuration: string[] = [];

    selectedItem &&
      selectedItem.dimensions &&
      selectedItem.dimensions.forEach((dimention) => {
        setDimension(dimention);
        if (dimention.isSelected) {
          configuration.push(`${dimention.id}.name`);
        }
      });
    selectedItem &&
      selectedItem.measures &&
      selectedItem.measures.forEach((dimention) => {
        configuration.push(`${dimention.id}`);
      });
    setDimension({ fieldName: "", id: virtualConfiguration.identifier, isSelected: true, selection: configuration });
    if (dataSourceId !== selectedItem?.parentId) {
      setDataSourceId?.(selectedItem?.parentId);
    }
    selectedItem && selectedItem.format && setFormat(BulkExportFormatEnum[selectedItem.format] as BulkExportFormat);
  }, [value]);
  const hierarchies = useMemo(() => {
    const allHierarchies = Hierarchy.from(
      items,
      (it) => String(it.id),
      (it) => String(it.id),

      (it) => it.name,
      (it) => String(it.parentId)
    );
    allHierarchies.forEach((h) => h.orderBy((it) => it.origin.id));
    return !search ? allHierarchies : Hierarchy.search(allHierarchies, search);
  }, [items]);
  const grouppedHierarchies = groupBy(hierarchies, (hierarchy) => hierarchy.origin.groupId);

  const classes = useStyles();
  const classesParent = useParentStyles();
  const classesItem = useItemStyles();
  const classesDisableItem = useDisabledItemStyles();

  const handleSelect = useCallback(
    (e: ChangeEvent<{}>, nodeId: string) => {
      const targetHierarchy = Hierarchy.flat(hierarchies).find((h) => h.id === nodeId);
      if (targetHierarchy !== undefined && !targetHierarchy.children?.length && targetHierarchy.origin.savingId !== savedExportSettingsId) {
        if (settingsWasModified) {
          setShowSaveNotification && setShowSaveNotification(true);
          setActionAfterSaveCancel &&
            setActionAfterSaveCancel(() => {
              setDataSourceId?.(targetHierarchy.origin.parentId);
              onChange?.({
                id: targetHierarchy.origin.savingId || 0,
                dimensions: targetHierarchy.origin.dimensions || [],
                format: ((targetHierarchy.origin.format && BulkExportFormatEnum[targetHierarchy.origin.format]) ||
                  BulkExportFormat.XLSX) as BulkExportFormat,
              });
            });
        } else {
          setDataSourceId?.(targetHierarchy.origin.parentId);
          onChange?.({
            id: targetHierarchy.origin.savingId || 0,
            dimensions: targetHierarchy.origin.dimensions || [],
            format: ((targetHierarchy.origin.format && BulkExportFormatEnum[targetHierarchy.origin.format]) ||
              BulkExportFormat.XLSX) as BulkExportFormat,
          });
        }
      }
    },
    [onChange, hierarchies, selectedItem, settingsWasModified, value]
  );

  const handleExpand = useCallback(
    (e: ChangeEvent<{}>, nodeIds: string[]) => {
      setExpanded(nodeIds);
    },

    [setExpanded]
  );
  const handleCloseDialog = () => {
    setShowSaveNotification && setShowSaveNotification(false);
    actionAfterSaveCancel && actionAfterSaveCancel();
    setSaved(false);
  };
  const handleSaveBulkExportState = useCallback(() => {
    setSettingsWasModified && setSettingsWasModified(false);
    saveExistExportSettings();
    setSaved(true);
  }, [setSaved, saveExistExportSettings, setSettingsWasModified]);

  const expandAllHierarcgies = (hierarchies: Hierarchy<string, ITreeControlItem>[]) => {
    const result: string[] = [];
    hierarchies.forEach((x) => {
      result.push(x.id);
      if (x.children) {
        result.push(...x.children.map((el) => el.id));
      }
    });
    return result;
  };

  const handlerFavorites = (type: FavouriteItemType, isFavourite: boolean, actionId: number) => {
    const params = { id: actionId, type, mark: { savedBulkExportSettingsId: actionId } };
    if (!isFavourite) {
      addFavourite(params);
    } else {
      removeFavourite({ type, id: actionId });
    }
  };

  useEffect(() => {
    if (favouriteItemId) {
      const results = Object.entries(grouppedHierarchies);
      const findIdInObjects = (results) => {
        let currentIds = [];
        results.forEach(([groupKey, groupHierarchies]) => {
          groupHierarchies.forEach((group) => {
            group.flatChildren.forEach((child) => {
              if (child?.origin?.savingId === favouriteItemId) {
                const parentId = child?.flatParents[1]?.id;
                const dataSetParentId = child?.flatParents[0]?.id;
                currentIds = [dataSetParentId as never, parentId as never];
              }
            });
          });
        });

        return currentIds;
      };
      const foundIds = findIdInObjects(results);
      setExpanded(foundIds);
    }
  }, [favouriteItemId]);

  return (
    <>
      <Dialog
        open={showSaveNotification}
        onCancel={handleCloseDialog}
        onApply={handleSaveBulkExportState}
        name={(selectedItem && selectedItem.name) || ""}
        saved={saved}
      />
      <TreeView
        className={classes.treeView}
        defaultCollapseIcon={<RemoveOutlinedIcon />}
        defaultExpandIcon={<AddBoxOutlinedIcon />}
        expanded={expandedAll ? expandAllHierarcgies(hierarchies) : expanded}
        selected={String(items.find((el) => el.savingId === value)?.id)}
        onNodeSelect={handleSelect}
        onNodeToggle={handleExpand}
      >
        {!hierarchies?.length ? "No results" : null}
        {Object.entries(grouppedHierarchies).map(([groupKey, groupHierarchies]) => (
          <div key={groupKey} className={classes.treeGroup}>
            {groupHierarchies.map((hierarchy) => (
              <TreeItem
                key={hierarchy.id}
                nodeId={hierarchy.id}
                label={removeCapitalization(hierarchy.name, ["APE"])}
                classes={classesParent}
              >
                {hierarchy.children.map((source) => (
                  <TreeItem
                    key={source.name}
                    nodeId={source.id}
                    label={removeCapitalization(source.name)}
                    classes={source.origin.disabled ? classesDisableItem : classesItem}
                  >
                    {source.children.map(({ origin, id, name }) => {
                      const isFavourite = Boolean(origin?.favoriteId);
                      const currentFavorite = isFavourite ? <StarRounded /> : <StarBorderRounded />;
                      const actionId = isFavourite ? origin?.favoriteId : origin?.savingId;
                      return (
                        <div className={classesItem.item}>
                          <TreeItem
                            key={`${name}_${id}`}
                            nodeId={id}
                            label={removeCapitalization(name)}
                            classes={origin.disabled ? classesDisableItem : classesItem}
                          />
                          <Button
                            variant="outlined"
                            className={classesItem.iconFavourite}
                            onClick={() => handlerFavorites(FavouriteItemType.SavedBulkExport, isFavourite, actionId!)}
                          >
                            {currentFavorite}
                          </Button>
                        </div>
                      );
                    })}
                  </TreeItem>
                ))}
              </TreeItem>
            ))}
          </div>
        ))}
      </TreeView>
    </>
  );
};
